/**
 * Admin "Users" panel — manage who can sign in to the admin portal.
 *
 * Three sections:
 *   1. Bootstrap (env vars, read-only) — emails/domains baked into ADMIN_EMAILS
 *      / ADMIN_DOMAINS / ADMIN_EMAIL. The current admin can't lock themselves
 *      out by accident from the UI.
 *   2. Runtime allowlist — emails or domains added at runtime; removable.
 *      Also surfaces "suspended" rows that block previously-allowed users.
 *   3. Logged-in users — actual /api/users rows with last login + suspend /
 *      unsuspend controls.
 *
 * Presentation is delegated to focused components under ./users/* — this file
 * owns the data + handlers and composes them.
 */
import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { startImpersonation } from "../../lib/api";
import AddAllowlistForm from "./users/AddAllowlistForm";
import { BootstrapTable, RuntimeAllowlistTable } from "./users/AllowlistTables";
import SignedInUsersTable from "./users/SignedInUsersTable";
import InvitationsTable from "./users/InvitationsTable";
import AccessAuditTable from "./users/AccessAuditTable";
import InviteModal from "./users/InviteModal";

export default function UsersPanel({ me: currentMe } = {}) {
  const [data, setData] = useState({ current_user_email: "", bootstrap: [], runtime: [], users: [] });
  const [invitations, setInvitations] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addType, setAddType] = useState("email");
  const [addValue, setAddValue] = useState("");
  const [addRole, setAddRole] = useState("sales_person");
  const [adding, setAdding] = useState(false);

  // Invite modal state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteNote, setInviteNote] = useState("");
  const [inviteRole, setInviteRole] = useState("sales_person");
  const [inviteTrialDays, setInviteTrialDays] = useState(7);
  const [inviting, setInviting] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, inv, aud] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/users/invitations").catch(() => ({ data: { items: [] } })),
        api.get("/admin/users/audit").catch(() => ({ data: { items: [] } })),
      ]);
      setData(data);
      setInvitations(inv.data?.items || []);
      setAudit(aud.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load users");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const submitAdd = async (e) => {
    e.preventDefault();
    const v = addValue.trim().toLowerCase().replace(/^@/, "");
    if (!v) return;
    if (addType === "email" && !v.includes("@")) {
      toast.error("Email must contain @");
      return;
    }
    if (addType === "domain" && (v.includes("@") || !v.includes("."))) {
      toast.error("Domain must look like example.com");
      return;
    }
    setAdding(true);
    try {
      const payload = { type: addType, value: v };
      if (addType === "email") payload.role = addRole;
      await api.post("/admin/users/allowlist", payload);
      toast.success(addType === "email" ? `Added ${v} as ${addRole.replace(/_/g, " ")}` : `Added domain: ${v}`);
      setAddValue("");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    } finally { setAdding(false); }
  };

  const removeEntry = async (entry) => {
    if (!window.confirm(`Remove ${entry.type} "${entry.value}" from the allowlist?`)) return;
    try {
      await api.delete(`/admin/users/allowlist/${entry.id}`);
      toast.success("Removed");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  const setRole = async (entry, role) => {
    try {
      await api.post("/admin/users/role", { email: entry.value, role });
      toast.success(`${entry.value} → ${role.replace(/_/g, " ")}`);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set role");
    }
  };

  const suspendUser = async (u) => {
    if (!window.confirm(`Suspend ${u.email}? They will be signed out immediately and blocked from signing back in.`)) return;
    try {
      await api.post(`/admin/users/${u.user_id}/suspend`);
      toast.success(`Suspended ${u.email}`);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Suspend failed");
    }
  };

  const unsuspendUser = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/unsuspend`);
      toast.success(`Restored ${u.email}`);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    }
  };

  const beginImpersonation = (u) => {
    if (!window.confirm(
      `Start acting as ${u.email}?\n\nAll API calls from now on will be evaluated as if this user made them. Every action will be audit-logged with BOTH your founder identity and their user_id. Exit anytime via the amber banner above the tabs.`
    )) return;
    startImpersonation(u.user_id);
    toast.success(`Now acting as ${u.email}`);
    // Reload so /api/me is refetched WITH the impersonation header and
    // every gated tab + panel re-renders against the impersonated role.
    setTimeout(() => window.location.reload(), 400);
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    const email = inviteEmail.trim().toLowerCase();
    if (!email.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    setInviting(true);
    try {
      const { data: res } = await api.post("/admin/users/invite", {
        email,
        name: inviteName.trim(),
        note: inviteNote.trim(),
        role: inviteRole,
        trial_days: inviteRole === "trial_user" ? Number(inviteTrialDays) || 7 : undefined,
      });
      if (res.email_sent) {
        toast.success(`Invitation sent to ${email}`);
      } else {
        toast.warning(`Added ${email} to the allowlist, but email was not sent (RESEND_API_KEY may be unset).`);
      }
      setInviteEmail("");
      setInviteName("");
      setInviteNote("");
      setInviteOpen(false);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    } finally { setInviting(false); }
  };

  const resendInvitation = async (inv) => {
    const days = inv.age_days != null ? inv.age_days : "?";
    if (!window.confirm(
      `Re-send the invitation to ${inv.email}?\n\n` +
      `Original sent ${days} day(s) ago by ${inv.invited_by || "—"}. ` +
      `They will receive a fresh email with your name as the inviter. ` +
      `A new audit row will be added.`
    )) return;
    setResendingId(inv.id);
    try {
      const { data: res } = await api.post(`/admin/users/invitations/${inv.id}/resend`);
      if (res.email_sent) {
        toast.success(`Reminder sent to ${inv.email}`);
      } else {
        toast.warning(`Resend recorded but email delivery failed (RESEND_API_KEY may be unset).`);
      }
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resend failed");
    } finally { setResendingId(null); }
  };

  const me = (data.current_user_email || "").toLowerCase();

  return (
    <div data-testid="admin-users-panel">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="ek-heading text-xl">Users &amp; access</h2>
          <p className="ek-text-muted text-[13px] mt-1 max-w-2xl">
            Anyone signing in via Emergent Google Auth with an email on this allowlist gets admin access.
            Add a colleague&apos;s email, an entire trusted domain (e.g. <code className="text-[12px] ek-text-soft">ekselens.com</code>),
            or suspend an existing user instantly. Bootstrap entries are read from <code className="text-[12px] ek-text-soft">ADMIN_EMAIL</code> /
            <code className="text-[12px] ek-text-soft">ADMIN_EMAILS</code> / <code className="text-[12px] ek-text-soft">ADMIN_DOMAINS</code> in <code className="text-[12px] ek-text-soft">backend/.env</code> and can&apos;t be removed here.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="ek-btn ek-btn-primary text-[12.5px]"
            data-testid="users-invite-open"
          >
            <Mail size={13} /> Invite a colleague
          </button>
          <button type="button" onClick={reload} className="ek-text-muted hover:text-black text-[12px]" data-testid="users-refresh">
            <RefreshCw size={12} className="inline mr-1" /> Refresh
          </button>
        </div>
      </div>

      <AddAllowlistForm
        addType={addType}
        setAddType={setAddType}
        addValue={addValue}
        setAddValue={setAddValue}
        addRole={addRole}
        setAddRole={setAddRole}
        adding={adding}
        onSubmit={submitAdd}
      />

      {loading ? (
        <p className="ek-text-muted text-[13px]">Loading…</p>
      ) : (
        <>
          <BootstrapTable rows={data.bootstrap} />
          <RuntimeAllowlistTable rows={data.runtime} onRemove={removeEntry} onSetRole={setRole} />
          <SignedInUsersTable
            users={data.users}
            me={me}
            currentMe={currentMe}
            onImpersonate={beginImpersonation}
            onSuspend={suspendUser}
            onUnsuspend={unsuspendUser}
          />
          <InvitationsTable invitations={invitations} resendingId={resendingId} onResend={resendInvitation} />
          <AccessAuditTable items={audit} />
        </>
      )}

      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        inviting={inviting}
        inviteEmail={inviteEmail}
        setInviteEmail={setInviteEmail}
        inviteName={inviteName}
        setInviteName={setInviteName}
        inviteNote={inviteNote}
        setInviteNote={setInviteNote}
        inviteRole={inviteRole}
        setInviteRole={setInviteRole}
        inviteTrialDays={inviteTrialDays}
        setInviteTrialDays={setInviteTrialDays}
        onSubmit={submitInvite}
      />
    </div>
  );
}
